import { useState } from 'react'
import type { Order } from '@alpha-edge/shared'
import {
  Modal,
  StatusBadge,
  btnPrimary,
  btnSecondary,
  colors,
  input,
  label as labelStyle,
  PrintLabelButton,
} from '../../components/ui'
import { api, ApiError } from '../../services/api'

interface AccessionResult {
  readonly id: string
  readonly accessionNumber: string
  readonly barcode: string
  readonly barcodeFormat: string
  readonly status: string
  readonly accessionedAt: string | null
}

interface AccessioningModalProps {
  readonly order: Order | null
  readonly patientName?: string
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onAccessioned: (result: AccessionResult) => void
}

function formatWait(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min} min`
  const hrs = Math.floor(min / 60)
  return `${hrs}h ${min % 60}m`
}

function DetailRow({ labelText, children }: { readonly labelText: string; readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 0',
        borderBottom: `1px solid ${colors.border}`,
        fontSize: '0.9375rem',
      }}
    >
      <span style={{ color: colors.textMuted, fontWeight: 500 }}>{labelText}</span>
      <span style={{ color: colors.text, textAlign: 'right' }}>{children}</span>
    </div>
  )
}

export function AccessioningModal({ order, patientName, isOpen, onClose, onAccessioned }: AccessioningModalProps) {
  const [specimenNotes, setSpecimenNotes] = useState(order?.specimenNotes ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accessionResult, setAccessionResult] = useState<AccessionResult | null>(null)

  if (!order) return null

  async function handleAccession() {
    if (!order) return
    setSubmitting(true)
    setError(null)
    try {
      // Save notes changes first if modified
      if (specimenNotes !== (order.specimenNotes ?? '')) {
        await api.put(`/orders/${order.id}`, { specimenNotes })
      }
      const res = await api.post<AccessionResult>(`/orders/${order.id}/accession`, {})
      if (!res.data) throw new Error('No data returned from accession endpoint')
      setAccessionResult(res.data)
      onAccessioned(res.data)
      // Don't auto-close: let the user optionally print a label first
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Accession failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Accession Order" width="560px">
      <div>
        <div
          style={{
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            background: '#f0f4ff',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: colors.text,
          }}
        >
          Confirm order details below. A barcode and accession number will be generated from your organization's
          configured format.
        </div>

        <DetailRow labelText="Order #">
          <strong>{order.orderNumber || order.id.slice(0, 8)}</strong>
        </DetailRow>
        {patientName && <DetailRow labelText="Patient">{patientName}</DetailRow>}
        <DetailRow labelText="Tests">{order.testTypes.join(', ')}</DetailRow>
        <DetailRow labelText="Priority">
          <StatusBadge status={order.priority} />
        </DetailRow>
        <DetailRow labelText="Provider">{order.orderingProviderName || '--'}</DetailRow>
        {order.diagnosisCodes && order.diagnosisCodes.length > 0 && (
          <DetailRow labelText="Diagnosis">{order.diagnosisCodes.join(', ')}</DetailRow>
        )}
        {order.submittedAt && <DetailRow labelText="Submitted">{formatWait(order.submittedAt)} ago</DetailRow>}

        <div style={{ marginTop: '1rem' }}>
          <label style={labelStyle}>Specimen Notes (optional)</label>
          <textarea
            value={specimenNotes}
            onChange={(e) => setSpecimenNotes(e.target.value)}
            rows={3}
            style={{ ...input, height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Collection notes, special handling instructions, etc."
          />
        </div>

        {error && (
          <div
            style={{
              marginTop: '0.75rem',
              color: colors.danger,
              padding: '0.5rem 0.75rem',
              background: colors.bgError,
              border: `1px solid ${colors.borderError}`,
              borderRadius: '3px',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            marginTop: '1.25rem',
            alignItems: 'center',
          }}
        >
          <button type="button" onClick={onClose} style={btnSecondary} disabled={submitting}>
            {accessionResult ? 'Close' : 'Cancel'}
          </button>
          {/* future: support `templates: readonly string[]` prop for multi-label accessioning */}
          {accessionResult && <PrintLabelButton orderId={order.id} onError={(e) => setError(e.message)} />}
          {!accessionResult && (
            <button type="button" onClick={handleAccession} style={btnPrimary} disabled={submitting}>
              {submitting ? 'Accessioning...' : 'Accession'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
